import React, { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import JoditEditor from "jodit-react";
import type { Jodit } from "jodit";
import { cn } from "lib/utils";

import ImagePicker from "../custom-features/image-picker/ImagePicker";
import AiTemplate from "../custom-features/ai/AiTemplate";
import {
  VariablePicker,
  useMentionDetection,
} from "../custom-features/mentions";
import { AI_SVG_ICON, GALLERY_ICON, PREVIEW_ICON } from "./icons";

const normalizeHtml = (s: string) =>
  (s || "")
    .replace(/\u200B/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .replace(/>\s+</g, "><")
    .trim();

const stripHtml = (html: string) => html.replace(/<[^>]*>?/gm, "");

/* ------------ props ------------ */
type TextEditorProps = {
  content: string;
  setContent: (value: string) => void;
  toggleView?: () => void;

  showTypingProgress?: boolean;
  maxCharCount?: number;
  templateId?: string | number;
};
export type TextEditorRef = unknown;

const TextEditor = forwardRef<TextEditorRef, TextEditorProps>(
  (
    {
      content = "",
      setContent,
      toggleView,
      showTypingProgress = true,
      maxCharCount = 1000,
      templateId,
    },
    _ref
  ) => {
    const editorRef = useRef<any>(null);
    const joditRef = useRef<Jodit | null>(null);

    const computeEditorHeight = () => {
      const isMobile = window.innerWidth < 640;
      return Math.max(320, window.innerHeight - (isMobile ? 196 : 248));
    };
    const [editorHeight, setEditorHeight] = useState(computeEditorHeight);

    useEffect(() => {
      const onResize = () => setEditorHeight(computeEditorHeight());
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [showModal, setShowModal] = useState(false);
    const aiTemplateRef = useRef<any>(null);
    const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);

    const [_isTyping, setIsTyping] = useState(false);
    const [_charPercentage, setCharPercentage] = useState(0);

    const mention = useMentionDetection();

    const toggleViewRef = useRef(toggleView);
    useEffect(() => {
      toggleViewRef.current = toggleView;
    }, [toggleView]);

    const isApplyingExternalRef = useRef(false);
    const lastEmittedRef = useRef<string>("");

    function setEditorValueSafely(j: Jodit, html: string) {
      const wys: HTMLElement | null = (j as any)?.editor?.editor ?? null;
      const scroller: HTMLElement | null =
        wys ?? document.querySelector<HTMLElement>(".jodit-wysiwyg");
      const prevScrollTop = scroller ? scroller.scrollTop : null;

      const sel: any = (j as any).s;
      let range: Range | null = null;
      try {
        const r = sel?.range;
        range = typeof r === 'function'
          ? (r.call(sel) as Range)
          : ((r as Range) ?? null);
      } catch {
        range = null;
      }

      isApplyingExternalRef.current = true;
      j.value = html;

      requestAnimationFrame(() => {
        if (scroller && prevScrollTop != null)
          scroller.scrollTop = prevScrollTop;
        if (range && sel?.selectRange) {
          try {
            sel.selectRange(range);
          } catch { }
        }
        isApplyingExternalRef.current = false;
      });
    }

    /* ------------ toolbar helpers ------------ */
    const setImageToEditor = (
      selectedImage: string,
      imgProps: Record<string, string>,
    ) => {
      let imgTag = imgProps.link
        ? `<a href="${imgProps.link}"><img src="https://${selectedImage}" `
        : `<img src="https://${selectedImage}" `;

      for (const [attrName, attrValue] of Object.entries(imgProps)) {
        if (attrValue && attrName !== "link")
          imgTag += ` ${attrName}="${attrValue}"`;
      }
      imgTag += imgProps.link ? "/></a> " : "/>";

      const j = joditRef.current;
      if (j?.selection?.insertHTML) {
        j.s?.save?.();
        j.selection.insertHTML(imgTag);
        const next = j.value;
        lastEmittedRef.current = next;
        setContent(next);
        updateTypingStats(next);
      } else {
        const next = (content || "") + imgTag;
        lastEmittedRef.current = next;
        setContent(next);
        updateTypingStats(next);
      }
      setShowModal(false);
    };

    const addAiTemplate = (html: string) => {
      const j = joditRef.current;
      if (j) {
        j.value = html;
        const next = j.value;
        lastEmittedRef.current = next;
        setContent(next);
        updateTypingStats(next);
      } else {
        lastEmittedRef.current = html;
        setContent(html);
        updateTypingStats(html);
      }
    };

    /* ------------ stable config (no dep on `content`) ------------ */
    const config = useMemo(() => {
      const buttons: string[] = [
        "source",
        "|",
        "imageUpload",
        "previewTemplate",
        "|",
        "bold",
        "italic",
        "underline",
        "|",
        "ul",
        "ol",
        "|",
        "outdent",
        "indent",
        "|",
        "left",
        "center",
        "right",
        "justify",
        "|",
        "link",
        "image",
        "|",
        "table",
        "hr",
        "|",
        "paragraph",
        "fontsize",
        "brush",
        "eraser",
        "|",
        "askWithAi",
      ];

      return {
        readonly: false,
        buttons,
        controls: {
          imageUpload: {
            name: "imageUpload",
            tooltip: "Upload Image",
            icon: GALLERY_ICON,
            exec: () => setShowModal(true),
          },
          previewTemplate: {
            name: "previewTemplate",
            tooltip: "Preview Template",
            icon: PREVIEW_ICON,
            exec: (editor: Jodit) => {
              if (editor?.value?.trim())
                toggleViewRef.current?.();
            },
          },
          askWithAi: {
            name: "askWithAi",
            tooltip: "Generate With AI",
            icon: AI_SVG_ICON,
            exec: () => aiTemplateRef.current?.openModal(),
          },
        },

        sourceEditor: "area" as const,
        textIcons: false,
        enter: "P" as const,
        brInPlainText: false,
        removeEmptyBlocks: false,
        placeholder: "Start crafting your email template here…",

        height: editorHeight,
        minHeight: 320,
        maxHeight: editorHeight,
        allowResizeX: false,
        allowResizeY: false,
        toolbarAdaptive: false,
        toolbarStickyOffset: 0,
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,

        iframe: false,
        useSearch: true,
        spellcheck: true,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        defaultActionOnPaste: "insert_clear_html",
        cleanHTML: { replaceNBSP: false },

        events: {
          afterInit: (j: Jodit) => {
            joditRef.current = j;
            if (typeof content === "string") {
              setEditorValueSafely(j, content);
              updateTypingStats(content);
            }
            requestAnimationFrame(() => {
              try {
                if (!j || (j as any).isDestructed) return;
                const wysiwyg: HTMLElement | null =
                  (j as any).editor?.editor ??
                  (j as any).editor ??
                  null;
                if (!wysiwyg) return;

                j.s?.setCursorIn?.(
                  wysiwyg.firstChild ?? (wysiwyg as any),
                  false,
                );

                wysiwyg.addEventListener(
                  "keyup",
                  mention.onJoditKeyup,
                );
                (j as any).__mentionCleanup = () =>
                  wysiwyg.removeEventListener(
                    "keyup",
                    mention.onJoditKeyup,
                  );
              } catch {
              }
            });
          },

          destroy: (j: Jodit) => {
            (j as any).__mentionCleanup?.();
          },

          beforeCommand: function (this: Jodit) {
            try {
              const wysiwyg: HTMLElement | null =
                (this as any).editor?.editor ??
                (this as any).editor ??
                null;
              if (!wysiwyg) return;
              const nativeSel =
                wysiwyg.ownerDocument?.getSelection?.();
              const needsReset =
                !nativeSel ||
                nativeSel.rangeCount === 0 ||
                !wysiwyg.contains(
                  nativeSel.getRangeAt(0)
                    ?.commonAncestorContainer,
                );
              if (needsReset) {
                wysiwyg.focus();
                this.s?.setCursorIn?.(
                  wysiwyg.firstChild ?? (wysiwyg as any),
                  false,
                );
              }
            } catch {
              /* non-critical */
            }
          },
        },
      } as const;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorHeight]);

    /* ------------ sync editor when prop `content` changes ------------ */
    useEffect(() => {
      const j = joditRef.current;
      if (!j) return;

      const nextNorm = normalizeHtml(content);
      const currNorm = normalizeHtml(j.value);
      const lastNorm = normalizeHtml(lastEmittedRef.current);
      if (nextNorm === lastNorm) return;

      if (nextNorm !== currNorm) {
        setEditorValueSafely(j, content || "");
      }
      updateTypingStats(content || "");
    }, [content]);

    /* ------------ user edits -> parent ------------ */
    const handleChange = React.useCallback(
      (val: string) => {
        if (isApplyingExternalRef.current) return;
        lastEmittedRef.current = val;
        setContent(val);
        updateTypingStats(val);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [setContent],
    );

    /* ------------ typing progress helpers ------------ */
    function updateTypingStats(html: string) {
      if (!showTypingProgress) return;
      const count = stripHtml(html || "").length;
      const pct =
        maxCharCount > 0
          ? Math.min((count / maxCharCount) * 100, 100)
          : 0;
      setIsTyping(count > 0);
      setCharPercentage(Math.round(pct));
    }

    return (
      <div className="relative w-full h-full flex flex-col min-h-0">
        <AiTemplate
          ref={aiTemplateRef}
          addAiTemplate={addAiTemplate}
          templateIcon
          onOpenChange={setIsAiPanelOpen}
          templateId={templateId}
          currentHtml={content}
        />

        <div
          id="customEditor"
          className={cn(
            "customEditor relative overflow-hidden transition-all duration-300 ease-in-out",
            isAiPanelOpen && "sm:mr-[320px]",
          )}
        >
          <JoditEditor
            ref={editorRef as any}
            config={config as any}
            onChange={handleChange}
          />
        </div>

        <ImagePicker
          showModal={showModal}
          handleClose={() => setShowModal(false)}
          setImageToEditor={setImageToEditor}
          activeTab="existing"
        />

        {/* @Variable picker — shown when user types @trigger */}
        {mention.isOpen && (
          <VariablePicker
            joditRef={joditRef}
            anchorEl={mention.anchorEl}
            query={mention.query}
            onClose={mention.close}
          />
        )}

        <style>{`
          /* ── List styles ─────────────────────────────────────────────── */
          .jodit-wysiwyg ul { list-style: disc; padding-left: 2rem; }
          .jodit-wysiwyg ol { list-style: decimal; padding-left: 2rem; }
          .jodit-wysiwyg li { list-style: inherit; }
          .jodit-container, .jodit-wysiwyg { overflow-anchor: none; }

          /* ── VariablePicker slide-in animation ───────────────────────── */
          @keyframes picker-in {
            from { opacity: 0; transform: translateY(-6px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0)    scale(1);    }
          }
          .animate-picker-in {
            animation: picker-in 140ms cubic-bezier(0.16, 1, 0.3, 1) both;
          }

          /* ── @Variable chips in editor (injected by VariablePicker) ─── */
          .jodit-wysiwyg .mention-variable {
            display: inline-flex;
            align-items: center;
            gap: 3px;
            padding: 1px 7px 1px 5px;
            margin: 0 2px;
            border-radius: 5px;
            background: linear-gradient(135deg, rgba(99,102,241,0.10), rgba(139,92,246,0.08));
            color: #4f46e5;
            font-size: 0.80em;
            font-weight: 700;
            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
            white-space: nowrap;
            border: 1px solid rgba(99,102,241,0.28);
            line-height: 1.7;
            vertical-align: middle;
            user-select: all;
            cursor: default;
            box-shadow: 0 1px 3px rgba(99,102,241,0.10);
          }
          .dark .jodit-wysiwyg .mention-variable {
            background: linear-gradient(135deg, rgba(99,102,241,0.20), rgba(139,92,246,0.15));
            color: #a5b4fc;
            border-color: rgba(99,102,241,0.35);
          }

          /* ── Toolbar shadow ──────────────────────────────────────────── */
          .jodit-toolbar__box {
            box-shadow: 0 1px 0 rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04);
          }

          /* ── Editor content area ─────────────────────────────────────── */
          .jodit-wysiwyg {
            overflow-anchor: none;
            font-size: 14px;
            line-height: 1.7;
            padding: 20px 24px !important;
          }

          /* ── Placeholder ───────────────────────────────────────────── */
          .jodit-placeholder {
            font-size: 14px !important;
            color: #9ca3af !important;
            font-style: italic;
            padding: 20px 24px !important;
          }
          .dark .jodit-placeholder {
            color: #475569 !important;
          }

          /* ── Scrollbar ─────────────────────────────────────────────── */
          .jodit-wysiwyg::-webkit-scrollbar { width: 4px; }
          .jodit-wysiwyg::-webkit-scrollbar-track { background: transparent; }
          .jodit-wysiwyg::-webkit-scrollbar-thumb {
            background: rgba(99,102,241,.20);
            border-radius: 4px;
          }
          .jodit-wysiwyg::-webkit-scrollbar-thumb:hover {
            background: rgba(99,102,241,.35);
          }

          /* ── Image containment ─────────────────────────────────────── */
          .jodit-wysiwyg img {
            max-width: 100% !important;
            display: block;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .jodit-wysiwyg img:not([height]):not([style*="height"]) {
            max-height: 420px;
            height: auto;
          }
          .jodit-wysiwyg [style*="width"] img,
          .jodit-wysiwyg table img {
            max-width: 100% !important;
          }

          /* ── Dark mode ──────────────────────────────────────────────────── */
          .dark .jodit-container {
            background: #0f1117 !important;
            border-color: #1e2536 !important;
          }
          .dark .jodit-toolbar__box {
            background: #161b27 !important;
            border-bottom: 1px solid #1e2536 !important;
          }
          .dark .jodit-toolbar-button__button {
            color: #94a3b8 !important;
          }
          .dark .jodit-toolbar-button__button:hover {
            background: #1e2a3a !important;
            color: #e2e8f0 !important;
          }
          .dark .jodit-toolbar-button__button.jodit-toolbar-button_is-active {
            background: #1d3461 !important;
            color: #60a5fa !important;
          }
          .dark .jodit-toolbar-button__trigger {
            color: #94a3b8 !important;
          }
          .dark .jodit-toolbar-button__separator {
            border-color: #1e2536 !important;
          }
          .dark .jodit-wysiwyg {
            background: #0f1117 !important;
            color: #e2e8f0 !important;
            caret-color: #818cf8;
          }
          .dark .jodit-wysiwyg ::selection {
            background: rgba(99,102,241,0.30);
          }
          .dark .jodit-status-bar {
            background: #161b27 !important;
            border-top: 1px solid #1e2536 !important;
            color: #475569 !important;
          }
          .dark .jodit-source__mirror {
            background: #0f1117 !important;
            color: #94a3b8 !important;
            border-color: #1e2536 !important;
          }
          .dark .jodit-popup__content {
            background: #161b27 !important;
            border-color: #1e2536 !important;
            box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
          }
          .dark .jodit-popup__content .jodit-toolbar__box {
            background: #1a2030 !important;
          }
          .dark .jodit-input {
            background: #1a2030 !important;
            border-color: #2d3748 !important;
            color: #e2e8f0 !important;
          }
        `}</style>
      </div>
    );
  }
);

TextEditor.displayName = "TextEditor";
export default TextEditor;
